import React, { useState, useEffect } from 'react';
import CustomSelect from './CustomSelect';
import MultiSelect from './MultiSelect';
import DatePicker from './DatePicker';
import PhoneInput from './PhoneInput';
import { validateWhatsAppNumber, getWhatsAppHint } from '../utils/whatsappValidator';

const DynamicField = ({
  field,
  value,
  onChange,
  error,
  autoFilled = false,
  isFulfilled = false
}) => {
  const [whatsappValidation, setWhatsappValidation] = useState(null);
  const [whatsappHint, setWhatsappHint] = useState(null);

  // Check if this is a WhatsApp field
  const isWhatsAppField = field.fieldKey === 'whatsapp' ||
                          field.name?.toLowerCase().includes('whatsapp');
  const getFieldClassName = () => {
    const classes = [];
    if (value && autoFilled) {
      classes.push('auto-filled');
    }
    if (field.required && isFulfilled) {
      classes.push('field-fulfilled');
    }
    if (error) {
      classes.push('field-error-input');
    }
    return classes.join(' ');
  };

  const getLabelClassName = () => {
    if (field.required && !isFulfilled) {
      return 'required';
    }
    if (field.required && isFulfilled) {
      return 'fulfilled';
    }
    return '';
  };

  // Validate WhatsApp number when value changes
  useEffect(() => {
    if (isWhatsAppField && value) {
      const validation = validateWhatsAppNumber(value);
      setWhatsappValidation(validation);

      const hint = getWhatsAppHint(value);
      setWhatsappHint(hint);
    } else if (isWhatsAppField) {
      setWhatsappValidation(null);
      setWhatsappHint('Include country code (e.g., +971 50 123 4567)');
    }
  }, [value, isWhatsAppField]);

  const handleChange = (e) => {
    onChange({
      target: {
        name: field.fieldKey,
        value: e.target.value
      }
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      onChange({
        target: {
          name: field.fieldKey,
          value: e.target.files[0]
        }
      });
    }
  };

  const renderField = () => {
    // Special handling for WhatsApp/Phone field
    if (field.type === 'tel' || isWhatsAppField) {
      return (
        <PhoneInput
          value={value || ''}
          onChange={onChange}
          name={field.fieldKey}
          placeholder={field.placeholder || 'Your WhatsApp number here'}
          required={field.required}
          error={error}
          autoFilled={autoFilled}
        />
      );
    }

    switch (field.type) {
      case 'text':
      case 'email':
      case 'url':
      case 'number':
        return (
          <input
            type={field.type === 'url' ? 'text' : field.type}
            name={field.fieldKey}
            value={value || ''}
            onChange={handleChange}
            required={field.required}
            placeholder={field.placeholder || `Enter ${field.name.toLowerCase()}`}
            className={getFieldClassName()}
          />
        );

      case 'textarea':
        // Convert textarea to text input
        return (
          <input
            type="text"
            name={field.fieldKey}
            value={value || ''}
            onChange={handleChange}
            required={field.required}
            placeholder={field.placeholder || `Enter ${field.name.toLowerCase()}`}
            className={getFieldClassName()}
          />
        );

      case 'date':
        return (
          <DatePicker
            value={value || ''}
            onChange={(dateValue) => {
              onChange({
                target: {
                  name: field.fieldKey,
                  value: dateValue
                }
              });
            }}
            error={error}
            placeholder={field.placeholder || 'Select date'}
          />
        );

      case 'select':
        return (
          <div className={getFieldClassName()}>
            <CustomSelect
              key={`${field.fieldKey}-${field.options?.length || 0}`}
              name={field.fieldKey}
              value={value || ''}
              onChange={onChange}
              options={field.options || []}
              required={field.required}
              placeholder=""
            />
          </div>
        );

      case 'multiselect':
        return (
          <div className={getFieldClassName()}>
            <MultiSelect
              name={field.fieldKey}
              value={value || []}
              onChange={(selectedValues) => onChange({
                target: {
                  name: field.fieldKey,
                  value: selectedValues
                }
              })}
              options={field.options || []}
              placeholder={field.placeholder || `Select ${field.name.toLowerCase()}`}
              className=""
            />
          </div>
        );

      case 'checkbox':
        return (
          <div className="checkbox-wrapper">
            <input
              type="checkbox"
              name={field.fieldKey}
              checked={value || false}
              onChange={(e) => onChange({
                target: {
                  name: field.fieldKey,
                  value: e.target.checked
                }
              })}
              required={field.required}
              style={{ accentColor: 'var(--astudio-black)' }}
            />
            <div className="checkbox-content">
              <p style={{ fontWeight: '500', marginBottom: '0' }}>
                {field.checkboxLabel || field.name}
              </p>
            </div>
          </div>
        );

      case 'radio':
        return (
          <div className={`radio-group ${getFieldClassName()}`}>
            {(field.options || []).map((option) => (
              <label key={option} className="radio-option">
                <input
                  type="radio"
                  name={field.fieldKey}
                  value={option}
                  checked={value === option}
                  onChange={handleChange}
                  required={field.required}
                  style={{ accentColor: 'var(--astudio-black)' }}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      case 'datetime':
        return (
          <input
            type="datetime-local"
            name={field.fieldKey}
            value={value || ''}
            onChange={handleChange}
            required={field.required}
            className={getFieldClassName()}
          />
        );

      case 'file':
        return (
          <div>
            <input
              type="file"
              name={field.fieldKey}
              onChange={handleFileChange}
              required={field.required}
              className={getFieldClassName()}
            />
          </div>
        );

      default:
        return (
          <input
            type="text"
            name={field.fieldKey}
            value={value || ''}
            onChange={handleChange}
            required={field.required}
            placeholder={field.placeholder || `Enter ${field.name.toLowerCase()}`}
            className={getFieldClassName()}
          />
        );
    }
  };

  // For checkbox fields, don't wrap in form-field div with label
  if (field.type === 'checkbox') {
    return (
      <div className="form-field" style={{ marginBottom: '20px' }}>
        {renderField()}
        {error && <div className="field-error">{error}</div>}
      </div>
    );
  }

  return (
    <div className="form-field">
      <label className={getLabelClassName()}>
        {field.name}
      </label>
      <div>{renderField()}</div>

      {/* Message area - always reserve space for alignment */}
      <div style={{ minHeight: '24px', marginTop: '6px' }}>
        {/* Error message */}
        {error && <div className="field-error" style={{ marginTop: '0' }}>{error}</div>}
      </div>
    </div>
  );
};

export default DynamicField;
